import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Download, Filter, TrendingUp, Users, Calendar,
  Loader2, RefreshCw, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import api from '@/api/api';
import * as XLSX from 'xlsx';
import { formatDate } from '../../utils/dateTimeUtils';

const PromotionReport = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sessions: [], courses: [] });
  const [stats, setStats] = useState({
    total_promotions: 0,
    unique_students: 0,
    semester_stats: {}
  });
  
  // Filter states
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCourse) params.append('course_id', selectedCourse);
      if (selectedSession) params.append('session', selectedSession);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      
      const response = await api.get(`/examination/promotion-report?${params.toString()}`);
      setPromotions(response.data.promotions || []);
      setStats({
        total_promotions: response.data.total_promotions || 0,
        unique_students: response.data.unique_students || 0,
        semester_stats: response.data.semester_stats || {}
      });
      setFilters(response.data.filters || { sessions: [], courses: [] });
    } catch (error) {
      console.error('Error fetching promotion report:', error);
      toast.error('Failed to load promotion report');
    } finally {
      setLoading(false);
    }
  }, [selectedCourse, selectedSession, fromDate, toDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const clearFilters = () => {
    setSelectedCourse('');
    setSelectedSession('');
    setFromDate('');
    setToDate('');
  };

  // Export to Excel
  const exportToExcel = () => {
    if (promotions.length === 0) {
      toast.error('No data to export');
      return;
    }

    const exportData = promotions.map((p, index) => ({
      'SL': index + 1,
      'Student Name': p.student_name || '-',
      'Admission No': p.admission_number || '-',
      'From Semester': p.from_semester_name || '-',
      'To Semester': p.to_semester_name || '-',
      'Session': p.session || '-',
      'Promoted By': p.promoted_by || '-',
      'Promotion Date': p.promoted_at ? formatDate(p.promoted_at) : '-'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Promotion Report');
    XLSX.writeFile(wb, `Promotion_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel file downloaded!');
  };

  // Bug fix: removed shadowing formatDate – using imported one

  return (
    <div className="space-y-6" data-testid="promotion-report-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Promotion Report</h1>
          <p className="text-gray-600 mt-1">View all student promotions with filters</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReport} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-blue-600 text-sm font-medium">Total Promotions</p>
                <p className="text-2xl font-bold text-blue-700">{stats.total_promotions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-green-600 text-sm font-medium">Unique Students</p>
                <p className="text-2xl font-bold text-green-700">{stats.unique_students}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-purple-600 text-sm font-medium">Semester Paths</p>
                <p className="text-2xl font-bold text-purple-700">{Object.keys(stats.semester_stats).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Semester Stats */}
      {Object.keys(stats.semester_stats).length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-700 mb-3">Semester-wise Promotions</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.semester_stats).map(([path, count]) => (
                <div key={path} className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">{path}</span>
                  <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            {(selectedCourse || selectedSession || fromDate || toDate) && (
              <Button variant="ghost" onClick={clearFilters} size="sm">
                Clear Filters
              </Button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Course</label>
                <select
                  className="w-full h-10 border border-gray-300 rounded-md px-3"
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                >
                  <option value="">All Courses</option>
                  {filters.courses.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Session</label>
                <select
                  className="w-full h-10 border border-gray-300 rounded-md px-3"
                  value={selectedSession}
                  onChange={(e) => setSelectedSession(e.target.value)}
                >
                  <option value="">All Sessions</option>
                  {filters.sessions.map(s => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">From Date</label>
                <input
                  type="date"
                  className="w-full h-10 border border-gray-300 rounded-md px-3"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">To Date</label>
                <input
                  type="date"
                  className="w-full h-10 border border-gray-300 rounded-md px-3"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Promotions Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading report...</span>
            </div>
          ) : promotions.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No promotions found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="promotion-report-table">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Admission No</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Promotion</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Session</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Promoted By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date & Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {promotions.map((promotion, index) => (
                    <tr key={promotion.id || index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {promotion.student_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-blue-600 font-semibold">
                        {promotion.admission_number || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded font-medium">
                            {promotion.from_semester_name || 'N/A'}
                          </span>
                          <ArrowRight className="w-4 h-4 text-green-600" />
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-medium">
                            {promotion.to_semester_name || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {promotion.session || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {promotion.promoted_by || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(promotion.promoted_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PromotionReport;
